import React, { useEffect } from 'react';
import {
  Button,
  Modal,
  ModalVariant,
  Form,
  FormGroup,
  Popover,
  TextInput,
  FormSelect,
  FormSelectOption,
  Spinner,
} from '@patternfly/react-core';
import { HelpIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import { useGetOrganizations } from 'src/query/orgs';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { nanoid } from 'nanoid';
import { API } from 'src/api';
import { checkUserRoleForOrg } from '@app/utils/checkUserRoles';
import { ClusterRepresentation, useGetRegions } from 'src/query/deployments';
import { useValidDeploymentName } from '@app/components/NamesValid';
import ButtonCustom from './button';
import { IconDeployment } from '@app/components/icons/icon-deployment';

type Props = {
  refresh: (val: string) => void;
  isModalOpen: boolean | ClusterRepresentation;
  setModalOpen: (open: boolean) => void;
};

export const CreateSharedDeployment: React.FC<Props> = ({ refresh, isModalOpen, setModalOpen }) => {
  const { checkSharedClusterDeploymentNameAvailability, nameValidityResponseText, clearNameValid } =
    useValidDeploymentName();
  const { isNameValid, nameHelperText } = nameValidityResponseText();
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm();

  // Get only shared regions
  const { data: regions = [] } = useGetRegions({
    queryParams: { shared: true },
    pathParams: { realm: 'self' },
  });

  const resetForm = () => {
    reset();
    clearNameValid();
  };

  const { data: orgs = [] } = useGetOrganizations(
    {
      pathParams: {
        realm: 'self',
      },
    },
    { refetchInterval: 5000, enabled: !!isModalOpen }
  );

  useEffect(() => {
    if (isModalOpen === false) {
      resetForm();
    }
  }, [isModalOpen]);

  const submitForm = async (dep) => {
    try {
      const toastId = toast.loading('Creating deployment. Please standby as this can take a few moments...');

      const createDeployment = await API.Orgs.createDeployment({
        name: dep.name,
        organization_id: dep.organization,
        region: dep.region,
      });

      toast.dismiss(toastId);
      if (createDeployment.ok) {
        toast.success(`${dep.name} deployment created successfully.`);
        setModalOpen(false);
        refresh(dep.name + nanoid());
        resetForm();
        return;
      }

      const errorResp = await createDeployment.json();
      toast.error(`${dep.name} deployment was not able to be created. (${errorResp.error})`);
    } catch (e) {
      console.log('[Error] Not able to create the deployment.');
    }
  };

  // Filter only orgs that user has the right role for
  const manageRoleOrgs = orgs.filter((org) => checkUserRoleForOrg('manage-organization', org.id));

  const ServiceAgreementText = () => (
    <div>
      <p>
        By creating a free Deployment in the Phase Two Cluster, you agree to be bound by the terms of our{' '}
        <a href="https://phasetwo.io/docs/service-agreement" target="_blank" rel="noreferrer">
          Service Agreement
        </a>
        . Deployments to the Phase Two Cluster are <b>intended for testing and development</b> and{' '}
        <b>not for production use</b> purposes. Inactive deployments in the Phase Two Cluster are subject to removal
        after a period of inactivity (sign-in required to keep the deployment active, notification will be sent).
      </p>
      <p className="mt-1">
        For <b>Production</b> workloads, please create a dedicated cluster.
      </p>
    </div>
  );

  const modalTitle = 'Create Deployment in Phase Two Cluster';

  return (
    <>
      <ButtonCustom
        onClick={() => setModalOpen(true)}
        light
        content={
          <>
            <IconDeployment height="28px" width="28px" /> <div>Create Deployment</div>
          </>
        }
      />
      <Modal
        variant={ModalVariant.medium}
        title={modalTitle}
        description={ServiceAgreementText()}
        isOpen={!!isModalOpen}
        onClose={() => setModalOpen(false)}
        actions={[
          <Button
            key="create"
            variant="primary"
            form="modal-with-form-create-deployment"
            type="submit"
            isDisabled={isSubmitting}
          >
            {isSubmitting ? (
              <div>
                <Spinner
                  isSVG
                  size="md"
                  aria-label="Contents of the small example"
                  style={{ '--pf-c-spinner--Color': 'white' }}
                />{' '}
                Creating deployment...
              </div>
            ) : (
              'Confirm'
            )}
          </Button>,
          <Button key="cancel" variant="link" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
        ]}
      >
        <Form id="modal-with-form-create-deployment" onSubmit={handleSubmit(submitForm)} style={{ marginTop: '2rem' }}>
          <FormGroup
            label="Region"
            labelIcon={
              <Popover headerContent={<div>Region</div>} bodyContent={<div>Region to deploy into.</div>}>
                <button
                  type="button"
                  aria-label="More info for region feld"
                  onClick={(e) => e.preventDefault()}
                  aria-describedby="modal-with-form-form-region"
                  className="pf-c-form__group-label-help"
                >
                  <HelpIcon noVerticalAlign />
                </button>
              </Popover>
            }
            isRequired
            fieldId="modal-with-form-form-region"
          >
            <Controller
              name="region"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormSelect id="region-choose-form" aria-label="Choose a region" isDisabled={isSubmitting} {...field}>
                  <FormSelectOption value="" label="Select one" isDisabled isPlaceholder />
                  {regions.map((option) => (
                    <FormSelectOption
                      key={option.region}
                      value={option.region}
                      label={`${option.provider?.toUpperCase()} - ${option.name} (${option.region})`}
                    />
                  ))}
                </FormSelect>
              )}
            />
          </FormGroup>
          <FormGroup
            label="Name"
            labelIcon={
              <Popover
                headerContent={<div>Name</div>}
                bodyContent={<div>The name of the realm. This must be unique across all Phase Two Deployments.</div>}
              >
                <button
                  type="button"
                  aria-label="More info for name field"
                  onClick={(e) => e.preventDefault()}
                  aria-describedby="modal-with-form-form-name"
                  className="pf-c-form__group-label-help"
                >
                  <HelpIcon noVerticalAlign />
                </button>
              </Popover>
            }
            isRequired
            fieldId="modal-with-form-form-name"
            validated={isNameValid}
            helperText={nameHelperText}
            helperTextInvalid={nameHelperText}
            helperTextInvalidIcon={<ExclamationCircleIcon />}
          >
            <Controller
              name="name"
              control={control}
              rules={{ required: true, min: 3 }}
              render={({ field }) => (
                <TextInput
                  id="modal-with-form-form-name"
                  isRequired
                  type="text"
                  validated={isNameValid}
                  onKeyUp={(e) => checkSharedClusterDeploymentNameAvailability(e.target.value)}
                  isDisabled={isSubmitting}
                  {...field}
                />
              )}
            />
          </FormGroup>
          <FormGroup
            label="Organization"
            labelIcon={
              <Popover
                headerContent={<div>Organization</div>}
                bodyContent={
                  <div>
                    This will assign the owner Organization. You must be a manager in this organization. All other
                    members of the organization will be able to fully use and administer this Deployment.
                  </div>
                }
              >
                <button
                  type="button"
                  aria-label="More info for organization field"
                  onClick={(e) => e.preventDefault()}
                  aria-describedby="modal-with-form-form-email"
                  className="pf-c-form__group-label-help"
                >
                  <HelpIcon noVerticalAlign />
                </button>
              </Popover>
            }
            isRequired
            fieldId="modal-with-form-form-email"
          >
            <Controller
              name="organization"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormSelect
                  id="organization-choose-form"
                  aria-label="Choose an organizaton"
                  isDisabled={isSubmitting}
                  {...field}
                >
                  <FormSelectOption value="" label="Select one" isDisabled isPlaceholder />
                  {manageRoleOrgs.map((option) => (
                    <FormSelectOption
                      key={option.id}
                      value={option.id}
                      label={`${option.displayName} (${option.id})`}
                    />
                  ))}
                </FormSelect>
              )}
            />
          </FormGroup>
        </Form>
      </Modal>
    </>
  );
};
